import dryerFireImage from "../../assets/posts/dryer_fire_smtfix.png";
import dryerOkImage from "../../assets/posts/dryer_ok.png";
import dishwasherDirtyImage from "../../assets/posts/dw_dirty.png";
import dishwasherOkImage from "../../assets/posts/dw_ok.png";
import gasRangeOrangeImage from "../../assets/posts/gas_range_flame_orange.png";
import gasRangeOkImage from "../../assets/posts/gas_range_ok.png";
import iceMachineDirtyImage from "../../assets/posts/icemachine_dirty_ice.png";
import iceMachineCleaningImage from "../../assets/posts/icemachine_cleanir_add.png";
import iceMachineOkImage from "../../assets/posts/icemachine_ok.png";
import washingMachineBadImage from "../../assets/posts/washing-machine-bad-condition.png";
import washingMachineOkImage from "../../assets/posts/washing-machine-ok.png";
import fridgeDirtyResultImage from "../../assets/posts/fridge_dirty_result.png";
import fridgeDirtyCoilsImage from "../../assets/posts/fridge_dirty_coils.png";
import fridgeOkImage from "../../assets/posts/fridge_ok.png";

export interface PostImage {
  kind: "image";
  src: string;
  alt: string;
  caption?: string;
}

export type PostMedia = PostImage;

export interface PostSection {
  id: string;
  heading: string;
  paragraphs: string[];
  media?: PostMedia[];
}

export interface PostCallToAction {
  heading: string;
  body: string;
  primaryButton: {
    label: string;
    href: string;
  };
}

export interface PostContent {
  slug: string;
  title: string;
  description: string;
  heroTitle: string;
  heroSubtitle?: string;
  heroMedia?: PostMedia | null;
  publishedAt: string;
  updatedAt?: string;
  readTimeMinutes: number;
  tags: string[];
  sections: PostSection[];
  callToAction?: PostCallToAction;
}

const BASE_POST_CTA: PostCallToAction = {
  heading: "Need appliance help today?",
  body: "Summit Fix dispatches certified techs across Orange County with same-day availability. Book online in minutes or call for immediate scheduling.",
  primaryButton: {
    label: "Schedule Appliance Repair",
    href: "/appointment",
  },
};

export const postsCollection: readonly PostContent[] = [
  {
    slug: "refrigerator-door-gasket-energy-loss",
    title: "Refrigerator Door Gaskets & Your Energy Bill",
    description:
      "Cracked or loose door gaskets let conditioned air escape, forcing compressors to run longer and driving up electricity costs.",
    heroTitle: "Stop Energy Waste with Tight Refrigerator Gaskets",
    heroSubtitle:
      "A failing door seal leaks cold air, warms perishables, and makes the compressor work overtime. Learn how to spot the issue and when to call Summit Fix.",
    heroMedia: {
      kind: "image",
      src: fridgeDirtyResultImage,
      alt: "Food spoiling inside a refrigerator due to temperature loss.",
      caption: "Warm spots and condensation inside the fridge often trace back to leaking or damaged door gaskets.",
    },
    publishedAt: "2025-02-25",
    readTimeMinutes: 6,
    tags: ["refrigerator repair", "energy efficiency", "maintenance"],
    sections: [
      {
        id: "why-gaskets-matter",
        heading: "Why door gaskets impact your electric bill",
        paragraphs: [
          "Door gaskets create an airtight barrier that keeps cold air inside the fridge and freezer. When they harden, crack, or pull away from the liner, conditioned air escapes every time the door closes.",
          "Escaping cold air means the compressor cycles more frequently to regain temperature, which can raise electricity usage by 10% or more in severe cases.",
          "Warm air infiltration also leads to frost buildup and moisture around shelves, inviting mold and shortening food life.",
        ],
        media: [
          {
            kind: "image",
            src: fridgeDirtyResultImage,
            alt: "Spoiled food and condensation inside a refrigerator.",
            caption: "Temperature swings and condensation signal the seal is not maintaining an airtight closure.",
          },
        ],
      },
      {
        id: "inspect-and-clean",
        heading: "How to inspect and care for refrigerator gaskets",
        paragraphs: [
          "Run the paper test: place a sheet of paper between the gasket and cabinet, close the door, and gently tug. If the paper slides out easily, the seal is weak.",
          "Clean the gasket monthly with mild detergent and warm water to remove crumbs and residue that keep the seal from sitting flush.",
          "Check for warped sections or torn corners. Even small gaps can leak significant amounts of cold air overnight.",
        ],
      },
      {
        id: "when-to-replace",
        heading: "When it is time for professional gasket replacement",
        paragraphs: [
          "If the gasket is brittle, cracked, or permanently misshapen, Summit Fix technicians will source the OEM part and install it so the door aligns correctly.",
          "We inspect door hinges, closing cams, and cabinet alignment to ensure the new gasket seals evenly across all sides.",
          "Technicians also verify internal temperatures and compressor run times after replacement to confirm the energy-saving fix.",
        ],
        media: [
          {
            kind: "image",
            src: fridgeOkImage,
            alt: "Refrigerator with a clean, tight door seal.",
            caption: "A fresh gasket keeps cold air in, food safe, and utility costs in check.",
          },
        ],
      },
    ],
    callToAction: {
      heading: "Need Summit Fix to replace your fridge gasket?",
      body: "Our technicians install new door seals, adjust hinges, and recalibrate temperatures so your refrigerator stays efficient.",
      primaryButton: {
        label: "Book Refrigerator Service",
        href: "/appointment",
      },
    },
  },
  {
    slug: "ice-machine-maintenance-and-repair",
    title: "Ice Machine Maintenance & Repair Essentials",
    description:
      "Keep ice machines sanitary, efficient, and problem-free by cleaning mineral build-up, sanitizing the reservoir, and monitoring production issues.",
    heroTitle: "Crystal-Clear Ice Starts with Regular Maintenance",
    heroSubtitle:
      "Mineral deposits and biofilm reduce ice production and compromise taste. Follow this Summit Fix routine or schedule professional service.",
    heroMedia: {
      kind: "image",
      src: iceMachineDirtyImage,
      alt: "Dirty ice machine bin with contaminated ice cubes.",
      caption: "Discoloration or cloudy cubes usually mean the machine needs immediate cleaning and sanitizing.",
    },
    publishedAt: "2025-02-24",
    readTimeMinutes: 8,
    tags: ["ice machine", "maintenance", "commercial"],
    sections: [
      {
        id: "routine-cleaning",
        heading: "Monthly cleaning keeps ice production consistent",
        paragraphs: [
          "Power down the machine, remove the ice bin, and discard remaining ice. Wipe the interior surfaces with a food-safe cleaner to remove slime and scale.",
          "Detach the water distribution components and soak them in manufacturer-approved cleaning solution to dissolve mineral deposits.",
          "Inspect the condenser intake for dust buildup. Restricted airflow forces the compressor to work harder and reduces the harvest cycle efficiency.",
        ],
        media: [
          {
            kind: "image",
            src: iceMachineDirtyImage,
            alt: "Dirty interior of an ice machine.",
            caption: "Neglected maintenance leads to scale buildup that contaminates cubes and stresses the refrigeration system.",
          },
        ],
      },
      {
        id: "cleaning-solution",
        heading: "Use the right cleaning solution and sanitizer",
        paragraphs: [
          "Mix the recommended amount of nickel-safe ice machine cleaner with warm water and circulate it through the water system following the manufacturer instructions.",
          "After descaling, run a sanitizing cycle to eliminate bacteria. Flush thoroughly with clean water to prevent chemical residue in the next batch of ice.",
          "Replace water filters on schedule. A clogged filter reduces water flow, creating smaller cubes and triggering low production alerts.",
        ],
        media: [
          {
            kind: "image",
            src: iceMachineCleaningImage,
            alt: "Applying ice machine cleaner solution during maintenance.",
            caption: "Professional cleaners flush mineral deposits from the evaporator grid and water distribution lines.",
          },
        ],
      },
      {
        id: "when-to-call-pros",
        heading: "When specialized repair is required",
        paragraphs: [
          "If production drops dramatically, cubes fuse together, or the machine leaks, Summit Fix technicians will inspect refrigerant pressures and the harvest mechanism.",
          "We disassemble and deep clean hard-to-reach sections, recalibrate water level sensors, and ensure the drain and pump systems operate correctly.",
          "Technicians also verify the bin thermostat, inlet water temperature, and compressor health to prevent recurring shutdowns.",
        ],
        media: [
          {
            kind: "image",
            src: iceMachineOkImage,
            alt: "Clean and properly maintained ice machine producing clear cubes.",
            caption: "A professionally serviced ice machine delivers clear, taste-free ice batch after batch.",
          },
        ],
      },
    ],
    callToAction: {
      heading: "Schedule Summit Fix ice machine service",
      body: "Our experts deep clean, sanitize, and repair commercial and residential ice makers to keep cubes clear and production on target.",
      primaryButton: {
        label: "Book Ice Machine Service",
        href: "/appointment",
      },
    },
  },
  {
    slug: "gas-range-maintenance-orange-flame-troubleshooting",
    title: "Gas Range Maintenance & Orange Flame Troubleshooting",
    description:
      "Learn why orange or yellow flames point to combustion problems, how to keep burners clean, and when Summit Fix technicians must adjust the fuel system.",
    heroTitle: "Restore Blue Flames for Safe Gas Range Cooking",
    heroSubtitle:
      "Orange flames signal incomplete combustion and potential carbon monoxide. Keep burners efficient with regular cleaning and expert calibration.",
    heroMedia: {
      kind: "image",
      src: gasRangeOrangeImage,
      alt: "Gas range burner with orange flame indicating improper combustion.",
      caption: "An orange flame shows the burner is starved of oxygen or contaminated with grease deposits.",
    },
    publishedAt: "2025-02-23",
    readTimeMinutes: 7,
    tags: ["gas range", "maintenance", "safety"],
    sections: [
      {
        id: "clean-burner-ports",
        heading: "Keep burner ports and igniters clean",
        paragraphs: [
          "Turn off gas, remove grates and burner caps, and soak them in warm soapy water to break down grease that deflects the flame pattern.",
          "Use a nylon brush or toothpick to clear each burner port opening. Never enlarge the ports—doing so changes the gas-to-air ratio.",
          "Wipe the igniter tip gently with a dry cloth to remove food residue. Dirty igniters cause delayed ignition that leads to flare-ups.",
        ],
      },
      {
        id: "watch-the-flame",
        heading: "Diagnose orange and yellow flames",
        paragraphs: [
          "A steady blue flame with a defined inner cone indicates proper combustion. Orange flickers usually mean airborne grease or dust is burning off.",
          "Persistent orange or yellow coloring, especially with soot on cookware, points to clogged burner orifices or incorrect air shutter settings.",
          "If you smell gas, hear popping, or see flames lifting off the burner, shut down the range immediately and schedule professional service.",
        ],
        media: [
          {
            kind: "image",
            src: gasRangeOrangeImage,
            alt: "Close-up of orange flame on gas burner.",
            caption: "Orange flames can produce soot and carbon monoxide—address the issue before cooking continues.",
          },
        ],
      },
      {
        id: "technician-adjustments",
        heading: "When specialized technicians are required",
        paragraphs: [
          "Summit Fix technicians remove and clean burner orifices, adjust shutter openings, and test gas pressure to ensure a stable blue flame.",
          "We inspect regulators, flex gas lines, and valve assemblies for leaks or damage that compromise safety.",
          "Technicians recalibrate oven thermostats and verify combustion inside sealed burner compartments common in professional-style ranges.",
        ],
        media: [
          {
            kind: "image",
            src: gasRangeOkImage,
            alt: "Properly tuned gas range with blue flame.",
            caption: "After calibration, burners produce an even blue flame for consistent cooking and improved safety.",
          },
        ],
      },
    ],
    callToAction: {
      heading: "Need Summit Fix to tune your gas range?",
      body: "Our specialists clean burners, adjust airflow, and verify fuel delivery so you can cook with confident blue flames.",
      primaryButton: {
        label: "Book Gas Range Service",
        href: "/appointment",
      },
    },
  },
  {
    slug: "dishwasher-maintenance-drain-hose-air-gap",
    title: "Dishwasher Maintenance & Drain Line Cleaning",
    description:
      "Routine cleaning keeps spray arms powerful, prevents backups in the drain hose, and stops odors from the air gap vent.",
    heroTitle: "Keep Dishwashers Fresh by Clearing Filters and Air Gaps",
    heroSubtitle:
      "Food soil and grease settle in filters and drain lines. Follow this Summit Fix routine to protect pumps and prevent standing water.",
    heroMedia: {
      kind: "image",
      src: dishwasherDirtyImage,
      alt: "Dishwasher interior with heavy grime and standing water.",
      caption: "Built-up food debris restricts spray arm flow and causes water to pool in the tub.",
    },
    publishedAt: "2025-02-21",
    readTimeMinutes: 6,
    tags: ["dishwasher", "maintenance", "kitchen"],
    sections: [
      {
        id: "weekly-cleaning",
        heading: "Weekly cleaning keeps spray arms and filters clear",
        paragraphs: [
          "Remove and rinse the primary filter cup under warm water. Use a soft brush to scrub mesh screens so food particles do not clog recirculation lines.",
          "Inspect upper and lower spray arms for blocked jets. Toothpicks or nylon brushes remove seeds and starch that cut wash pressure.",
          "Run an empty hot cycle with dishwasher cleaner once a month to dissolve grease on the interior tub and neutralize odors.",
        ],
      },
      {
        id: "drain-hose-air-gap",
        heading: "Clean the drain hose and air gap every season",
        paragraphs: [
          "Disconnect power, remove the toe kick, and detach the drain hose from the pump outlet. Flush the hose with hot water to clear soap scum and food residue.",
          "If your sink has an air gap, twist off the cap, pull out the insert, and scrub away the biofilm that traps odors and causes backups onto countertops.",
          "Verify the hose loops higher than the disposal inlet or standpipe. A high loop prevents dirty sink water from siphoning back into the tub.",
        ],
        media: [
          {
            kind: "image",
            src: dishwasherDirtyImage,
            alt: "Dishwasher drain line clogged with grease and food debris.",
            caption: "A restricted drain hose leaves murky water at the bottom of the unit after every cycle.",
          },
        ],
      },
      {
        id: "when-to-call-pro",
        heading: "When professional service is essential",
        paragraphs: [
          "Persistent drain errors, leaks at the pump housing, or standing water after cleaning the hose point to a failing drain pump that needs replacement.",
          "If the air gap overflows during every cycle, Summit Fix technicians will snake the drain line, check the disposal knockout, and confirm the installation meets code.",
          "Our specialists also recalibrate sensor-controlled wash cycles, replace worn door gaskets, and sanitize hidden sump components that homeowners cannot reach safely.",
        ],
        media: [
          {
            kind: "image",
            src: dishwasherOkImage,
            alt: "Clean dishwasher interior with sparkling dishes.",
            caption: "Regular maintenance and professional inspections keep the tub spotless and cycles efficient.",
          },
        ],
      },
    ],
    callToAction: {
      heading: "Schedule a Summit Fix dishwasher tune-up",
      body: "We deep clean filters, flush drain hoses, and service air gaps so your dishwasher drains fast and smells fresh.",
      primaryButton: {
        label: "Book Dishwasher Service",
        href: "/appointment",
      },
    },
  },
  {
    slug: "refrigerator-condenser-coil-maintenance",
    title: "Refrigerator Maintenance: Cleaning Condenser Coils",
    description:
      "Dusty condenser coils force compressors to overheat, causing temperature swings and spoiled food. Learn how Summit Fix keeps coils efficient and safe.",
    heroTitle: "Stop Fridge Temperature Swings with Coil Cleaning",
    heroSubtitle:
      "Regular coil cleaning cuts energy costs and extends compressor life. Use this Summit Fix checklist to keep your refrigerator cooling at factory specs.",
    heroMedia: {
      kind: "image",
      src: fridgeDirtyResultImage,
      alt: "Refrigerator with poor cooling performance due to dirty coils.",
      caption: "Blocked airflow around the condenser coils leads to warm spots and overworked compressors.",
    },
    publishedAt: "2025-02-22",
    readTimeMinutes: 7,
    tags: ["refrigerator repair", "maintenance", "energy efficiency"],
    sections: [
      {
        id: "why-coils-matter",
        heading: "Why condenser coils need seasonal cleaning",
        paragraphs: [
          "Condenser coils release heat pulled from inside the fresh food and freezer cabinets. When dust blankets the coils, airflow stalls and refrigerant cannot cool down before circulating back through the system.",
          "The compressor then runs longer at higher pressures, driving up energy bills and risking premature failure of sealed system components.",
          "Dirty coils also cause temperature variances inside the fridge, leading to soft ice cream, wilting produce, and food safety concerns.",
        ],
        media: [
          {
            kind: "image",
            src: fridgeDirtyResultImage,
            alt: "Food inside a refrigerator spoiling due to temperature issues.",
            caption: "Persistent warm zones signal the condenser cannot shed heat because the coil is choked with dust.",
          },
        ],
      },
      {
        id: "cleaning-steps",
        heading: "How to clean condenser coils safely",
        paragraphs: [
          "Unplug the refrigerator, remove the toe-kick grille or rear access cover, and use a vacuum with a crevice tool to lift surface dust.",
          "Brush stubborn debris off each coil loop with a soft, non-metal brush. Work top to bottom to avoid pushing grime deeper into the coil pack.",
          "Inspect the condenser fan for lint on the blades and ensure the fan spins freely. Restricted fans compound airflow problems even after the coil is clean.",
        ],
        media: [
          {
            kind: "image",
            src: fridgeDirtyCoilsImage,
            alt: "Close-up of refrigerator condenser coils packed with dust.",
            caption: "Packed dust insulates the coil and starves the compressor of critical cooling airflow.",
          },
        ],
      },
      {
        id: "when-to-call-pro",
        heading: "When to call Summit Fix technicians",
        paragraphs: [
          "If the coils are inaccessible, the unit is built into cabinetry, or the compressor has tripped thermal overloads, professional servicing is safer and faster.",
          "Technicians degrease coils, verify sealed system pressures, and recalibrate temperature sensors to restore food-safe cooling.",
          "Summit Fix also checks door gaskets, evaporator fan operation, and defrost systems that contribute to temperature instability.",
        ],
        media: [
          {
            kind: "image",
            src: fridgeOkImage,
            alt: "Clean refrigerator condenser coils with proper airflow.",
            caption: "After a professional coil cleaning, airflow returns and the compressor runs cooler and quieter.",
          },
        ],
      },
    ],
    callToAction: {
      heading: "Schedule a Summit Fix refrigerator tune-up",
      body: "Our technicians clean coils, inspect fans, and recalibrate sensors so your refrigerator holds steady temperatures year-round.",
      primaryButton: {
        label: "Book Fridge Service",
        href: "/appointment",
      },
    },
  },
  {
    slug: "washing-machine-maintenance-clean-drum-filter",
    title: "Washing Machine Maintenance & Ventilation Checklist",
    description:
      "Keep front-load and top-load washers fresh, efficient, and leak free by staying ahead of lint trap clogs, moldy door boots, and drum residue.",
    heroTitle: "Stop Washer Odors with Filter, Seal, and Drum Care",
    heroSubtitle:
      "Standing water and trapped lint create the perfect environment for mildew. Follow this Summit Fix maintenance plan to keep cycles clean and safe.",
    heroMedia: {
      kind: "image",
      src: washingMachineBadImage,
      alt: "Illustration of a washer with lint-clogged filter and water around the machine.",
      caption: "Lint, detergent residue, and standing water collect around the filter and door boot when maintenance slips.",
    },
    publishedAt: "2025-02-20",
    readTimeMinutes: 8,
    tags: ["washing machine", "maintenance", "cleaning"],
    sections: [
      {
        id: "clean-the-filter",
        heading: "Clean the drain filter every month",
        paragraphs: [
          "Shut off power, open the access panel, and place a shallow tray or towel below the drain filter cap to catch trapped water.",
          "Remove the filter cartridge, clear lint and debris, and rinse it under warm water. Inspect the housing for coins or hair pins that damage the circulation pump.",
          "Reinstall the filter snugly to prevent leaks. Leaving moisture in the cavity invites mold growth that travels into the wash drum.",
        ],
        media: [
          {
            kind: "image",
            src: washingMachineBadImage,
            alt: "View of a washer filter clogged with lint and standing water.",
            caption: "A clogged filter forces the pump to overwork and leaves gray water backing up into the tub.",
          },
        ],
      },
      {
        id: "protect-door-seal",
        heading: "Dry the door boot seal after every cycle",
        paragraphs: [
          "Wipe moisture and detergent suds from the inner door boot seal with a microfiber cloth. Leaving the door cracked open speeds evaporation.",
          "Inspect the boot for trapped lint, pet hair, or foreign objects that pinch holes in the rubber and lead to front panel leaks.",
          "Plan to replace the boot seal every three to five years, or sooner if cracks, mildew stains, or persistent odors appear. Professional replacement ensures the seal is seated and tensioned correctly.",
        ],
      },
      {
        id: "deep-clean-drum",
        heading: "Schedule seasonal drum sanitizing",
        paragraphs: [
          "Run a monthly hot maintenance cycle with manufacturer-approved tablets to dissolve detergent buildup and biofilm on the stainless drum.",
          "For heavy soil, Summit Fix technicians disassemble the agitator, clean the recirculation lines, and flush the outer tub with pressurized cleaners.",
          "Technicians verify the drain hose routing and standpipe height to prevent siphoning, which leaves dirty water behind after each cycle.",
        ],
      },
      {
        id: "when-to-call-technicians",
        heading: "When specialists need to step in",
        paragraphs: [
          "Persistent leaks around the door, soft spots in the boot seal, or rust at the bottom of the cabinet indicate internal damage that requires a trained technician.",
          "Unbalanced spin cycles, banging noises, or drain errors after a filter cleaning often point to failing bearings or pumps that need OEM replacements.",
          "Summit Fix washer specialists recalibrate load-sensing systems, replace worn seals and hoses, and ensure the machine sits level with proper vibration dampening.",
        ],
        media: [
          {
            kind: "image",
            src: washingMachineOkImage,
            alt: "Well-maintained washer with clean drum and door boot seal.",
            caption: "A cleaned filter, dry door boot, and balanced drum keep washers running quietly for years.",
          },
        ],
      },
    ],
    callToAction: {
      heading: "Need a professional washer tune-up?",
      body: "Summit Fix technicians clean filters, replace door boots, and sanitize drums so your washer stays leak-free and odorless.",
      primaryButton: {
        label: "Book Washer Service",
        href: "/appointment",
      },
    },
  },
  {
    slug: "dryer-vent-maintenance-fire-safety",
    title: "Dryer Maintenance & Vent Cleaning Checklist",
    description:
      "A seasonal plan to clear lint, clean vents, and keep dryers running safely so your home avoids preventable fire hazards.",
    heroTitle: "Prevent Dryer Fires with a Proven Maintenance Routine",
    heroSubtitle:
      "Built-up lint is the number one cause of dryer fires. Follow this Summit Fix checklist to improve airflow, efficiency, and home safety.",
    heroMedia: {
      kind: "image",
      src: dryerFireImage,
      alt: "Illustration of clogged dryer vent causing a fire hazard.",
      caption: "Clogged vents trap heat and can ignite lint inside the ductwork.",
    },
    publishedAt: "2025-02-18",
    readTimeMinutes: 7,
    tags: ["dryer repair", "maintenance", "fire safety"],
    sections: [
      {
        id: "why-lint-builds-up",
        heading: "Why dryer lint builds up so quickly",
        paragraphs: [
          "Every load of laundry sheds fibers. When lint bypasses the lint screen or the filter is already packed, it rides the moist exhaust stream toward the vent. Bends in the duct, long runs, and flexible foil tubing act like lint magnets.",
          "As lint collects, the dryer has to run hotter and longer to push moisture out. The cabinet's thermal fuse and safety sensors struggle to keep temperatures in check, creating the perfect fuel-and-oxygen mix for a flash fire.",
        ],
        media: [
          {
            kind: "image",
            src: dryerFireImage,
            alt: "Illustration of dryer fire caused by clogged vent.",
            caption: "Heat trapped behind lint-choked vent runs can ignite the duct or surrounding cabinets.",
          },
        ],
      },
      {
        id: "monthly-checklist",
        heading: "Monthly airflow and lint removal checklist",
        paragraphs: [
          "Clean the lint screen before and after every cycle. Rinse it with mild soap monthly to remove dryer sheet residue that restricts airflow.",
          "Inspect visible vent tubing for kinks or crushed sections. Straighten rigid duct and replace foil flex hose with UL-listed rigid or semi-rigid vent pipe.",
          "Vacuum behind the dryer and around the vent transition every 30 days. Pay attention to the floor and baseboards where lint drifts pile up unnoticed.",
        ],
      },
      {
        id: "seasonal-deep-clean",
        heading: "Seasonal deep cleaning: what homeowners can DIY",
        paragraphs: [
          "Disconnect power, detach the dryer vent, and run a brush kit or vacuum attachment through the entire duct line to pull out compacted lint.",
          "Clean the moisture sensor bars inside the drum with rubbing alcohol so the control board stops the cycle when clothes are actually dry.",
          "Check that the exterior vent flap swings freely and has a bird guard. A stuck flap traps moisture and lint right at the wall penetration.",
        ],
      },
      {
        id: "when-to-call-pros",
        heading: "Know when a pro cleaning is mandatory",
        paragraphs: [
          "Dryer feels hot to the touch, cycles take longer, or the laundry room smells musty - classic signs that the vent run is partially blocked beyond DIY reach.",
          "The vent exits through the roof or behind finished walls. Professional cleaning forces lint out with rotary brushes and negative-air vacuums designed for long duct runs.",
          "You see scorch marks, melted flex hose, or tripped thermal fuses. Summit Fix technicians can restore safe operation and ensure the vent route meets current code.",
        ],
        media: [
          {
            kind: "image",
            src: dryerOkImage,
            alt: "Clean dryer vent setup with proper airflow.",
            caption: "A clean, rigid vent path keeps air moving freely and protects the motor, heater, and your home.",
          },
        ],
      },
    ],
    callToAction: {
      heading: "Schedule a Summit Fix dryer vent cleaning",
      body: "Our certified technicians clear lint throughout the vent line, calibrate sensors, and verify safe airflow so you never worry about dryer fires.",
      primaryButton: {
        label: "Book Dryer Service",
        href: "/appointment",
      },
    },
  },
  {
    slug: "appliance-maintenance-checklist",
    title: "Seasonal Appliance Maintenance Checklist",
    description:
      "Keep your kitchen and laundry appliances running longer with this preventative maintenance checklist used by Summit Fix technicians.",
    heroTitle: "Seasonal Appliance Maintenance Checklist",
    heroSubtitle:
      "A practical guide our technicians share with homeowners to prevent breakdowns, reduce energy spikes, and avoid costly repairs.",
    publishedAt: "2025-01-12",
    updatedAt: "2025-01-24",
    readTimeMinutes: 8,
    tags: ["maintenance", "homeowner tips", "energy efficiency"],
    sections: [
      {
        id: "why-maintenance-matters",
        heading: "Why appliance maintenance matters",
        paragraphs: [
          "Appliances rarely fail without a warning sign. Small changes in sound, cycle length, or temperature often point to restricted airflow, worn seals, or mineral build-up.",
          "Handling these issues seasonally lowers power bills, catches parts that are wearing down, and protects manufacturer warranties that require documented upkeep.",
        ],
      },
      {
        id: "quarterly-tasks",
        heading: "Quarterly tasks for every household",
        paragraphs: [
          "Inspect refrigerator door seals for gaps and clean with a mild detergent to maintain a proper seal.",
          "Run a hot cycle with a descaling agent on dishwashers and washers to cut hard-water deposits on hidden components.",
          "Vacuum refrigerator condenser coils and the dryer exhaust path to prevent compressor overwork and dryer heating faults.",
        ],
      },
      {
        id: "seasonal-schedule",
        heading: "Suggested seasonal maintenance schedule",
        paragraphs: [
          "Spring: Replace refrigerator water filters, clean range hood grease filters, and test smoke or CO detectors near appliances.",
          "Summer: Flush ice makers, inspect window and door seals around HVAC-connected appliances, and test refrigerator condenser fans for dust blocking.",
          "Fall: Check oven calibration before holiday cooking, inspect dryer vent termination outside the home, and clean washer drain traps.",
          "Winter: Review surge protection for premium appliances, inspect sump pumps connected to laundry rooms, and schedule professional maintenance before peak usage.",
        ],
      },
      {
        id: "when-to-call-pro",
        heading: "When DIY stops and pros step in",
        paragraphs: [
          "If breakers trip repeatedly, temperatures swing more than 10 degrees, or lights dim when appliances start, professional diagnostics are safer than trial-and-error fixes.",
          "Summit Fix technicians use manufacturer diagnostics and inventory OEM parts on their vans, which shortens downtime on premium and built-in appliances.",
        ],
      },
    ],
    callToAction: BASE_POST_CTA,
  },
  {
    slug: "dryer-not-heating-troubleshooting",
    title: "Dryer Not Heating? A Troubleshooting Guide",
    description:
      "Step-by-step checks homeowners can perform when an electric or gas dryer stops heating, plus signs it is time to call Summit Fix.",
    heroTitle: "Dryer Not Heating? Start with These Checks",
    heroSubtitle:
      "From clogged vents to failed heating elements, learn how to isolate the root cause before calling for service.",
    publishedAt: "2025-02-04",
    readTimeMinutes: 6,
    tags: ["dryer repair", "troubleshooting", "laundry"],
    sections: [
      {
        id: "verify-power",
        heading: "Start by verifying power and settings",
        paragraphs: [
          "Confirm the breaker is fully seated in the ON position - dryers use two linked breakers and one side can trip independently.",
          "Inspect the control panel settings. Eco or wrinkle-prevent cycles sometimes run without full heat; switch to a timed dry cycle to test.",
        ],
      },
      {
        id: "check-airflow",
        heading: "Check for restricted airflow",
        paragraphs: [
          "Remove lint from the lint screen and rinse it under warm water if fabric softener residue builds up.",
          "Disconnect the vent hose and inspect it for crushed sections or heavy lint debris. Run the dryer briefly with the hose disconnected - if heat returns, vent cleaning is required.",
        ],
      },
      {
        id: "component-failures",
        heading: "Common component failures Summit Fix repairs",
        paragraphs: [
          "Thermal fuse or high-limit thermostat: fails closed when high heat is detected, cutting power to the element.",
          "Heating element or gas igniter: breaks after repeated stress cycles or heavy lint buildup around the housing.",
          "Moisture sensor bars: covered in residue causing the control board to end cycles prematurely, creating the appearance of no heat.",
        ],
      },
      {
        id: "call-for-service",
        heading: "When to call Summit Fix",
        paragraphs: [
          "If breakers trip immediately after resetting, a shorted element or motor issue needs a licensed technician.",
          "Gas dryers with no flame, repeated ignition clicks, or raw gas smells require immediate professional diagnostics.",
          "Summit Fix provides same-day diagnostics in Orange County with OEM parts for gas and electric dryers across premium brands.",
        ],
      },
    ],
    callToAction: BASE_POST_CTA,
  },
] as const;

export const postsBySlug = new Map(postsCollection.map((post) => [post.slug, post]));

export const getPostBySlug = (slug: string | null | undefined): PostContent | undefined => {
  if (!slug) {
    return undefined;
  }

  return postsBySlug.get(slug);
};

export const POSTS_BASE_URL = "https://smtfixus.com/posts";
